import { React, useEffect, useState } from 'react';
import { useAuth0 } from '@auth0/auth0-react';
import {
    ResponsiveChartContainer,
    BarPlot,
    LinePlot,
    MarkPlot,
    ChartsTooltip,
    ChartsYAxis
} from '@mui/x-charts';
import {
    Box, CircularProgress, Grid,
    Table, TableBody, TableCell, TableContainer, TableRow,
    Card, CardHeader, CardContent, Dialog, DialogTitle, DialogContent, IconButton,
    Chip, Stack, Typography
} from '@mui/material';
import CloseIcon from '@mui/icons-material/CloseRounded';
import {
    DataGridPro,
    GridToolbar
} from '@mui/x-data-grid-pro';
import { formatCurrency, formatNumber, formatNumberFraction } from '../utils/utils.ts';
import axios from 'axios';
import dayjs from 'dayjs';

var api = axios.create({ withCredentials: true });

function Events(props) {
    const { getAccessTokenSilently } = useAuth0();
    const [columns, setColumns] = useState(null);
    const [rows, setRows] = useState(null);
    const [marketColumns, setMarketColumns] = useState(null);
    const [marketRows, setMarketRows] = useState(null);
    const [allMarketRows, setAllMarketRows] = useState(null);
    const [visibleDataSources, setVisibleDataSources] = useState({
        my_tickets: true,
        my_sales: true,
        market_listings: true,
        market_sales: true
    });
    const [event, setEvent] = useState(null);
    const event_id = props.event_id;
    const current_broker = props.current_broker;
    const isConnected = props.isConnected;
    const [openDialog, setDialogOpen] = useState(false);
    const [ecolumns, setEColumns] = useState(null);
    const [erows, setERows] = useState(null);
    const [clicked_date, setClickedDate] = useState(dayjs().format('MM/DD/YYYY'));

    const handleDialogClickOpen = () => {
        setDialogOpen(true);
    };
    const handleDialogClose = () => {
        setDialogOpen(false);
        setClickedDate(dayjs().format('MM/DD/YYYY'));
        setEColumns(null);
        setERows(null);
    };

    function getEvent() {
        if (event_id) {
            getAccessTokenSilently({
                audience: process.env.REACT_APP_AUTH0AUDIENCE,
                scope: process.env.REACT_APP_AUTH0SCOPE
            }).then((token) => {
                api.get(
                    `${process.env.REACT_APP_BASEURL}/api/dashboard/event/${event_id}/${current_broker}`,
                    { headers: { Authorization: `Bearer ${token}` } }
                ).then((response) => {
                    setEvent(response.data);

                    var c = [
                        { field: 'count', headerName: 'Open Qty', flex: .25, minWidth: 50, type: 'number' },
                        { field: 'scount', headerName: 'Sold Qty', flex: .25, minWidth: 50, type: 'number' },
                        { field: 'section', headerName: 'Section', flex: .25, minWidth: 50, type: 'string' },
                        { field: 'row', headerName: 'Row', flex: .25, minWidth: 50, type: 'string' },
                        { field: 'low', headerName: 'Low Seat', flex: .25, minWidth: 50, type: 'number' },
                        { field: 'high', headerName: 'High Seat', flex: .25, minWidth: 50, type: 'number' },
                        {
                            field: 'cost', headerName: 'Open Cost', flex: .25, minWidth: 100, type: 'number',
                            renderCell: (params) => {
                                return `${formatCurrency(params.value)}`;
                            }
                        },
                        {
                            field: 'retail', headerName: 'Retail', flex: .25, minWidth: 100, type: 'number',
                            renderCell: (params) => {
                                return `${formatCurrency(params.value)}`;
                            }
                        },
                        {
                            field: 'avg', headerName: 'Retail Avg Ticket', flex: .25, minWidth: 125, type: 'number',
                            renderCell: (params) => {
                                return `${formatCurrency(params.value)}`;
                            }
                        },
                    ];
                    setColumns(c);

                    var r = [];
                    response.data.open_tickets.forEach((row, x) => {
                        var sold_count = 0;
                        response.data.sold_tickets.forEach((s) => {
                            if (s._id.section === row._id.section && s._id.row === row._id.row) {
                                sold_count = s.sold_count;
                            }
                        });
                        r.push({
                            id: x,
                            count: row.open_count,
                            scount: sold_count,
                            section: row._id.section,
                            row: row._id.row,
                            low: row.low_seat,
                            high: row.high_seat,
                            cost: row.cost,
                            retail: row.retail,
                            avg: row.avg_ticket
                        });
                    });
                    setRows(r);

                    // Process market comparison data
                    var mc = [
                        { field: 'section', headerName: 'Section', flex: .15, minWidth: 80, type: 'string' },
                        { field: 'row', headerName: 'Row', flex: .15, minWidth: 80, type: 'string' },
                        { field: 'qty', headerName: 'Qty', flex: .15, minWidth: 60, type: 'number' },
                        {
                            field: 'price_per_ticket', headerName: 'Price Per Ticket', flex: .25, minWidth: 120, type: 'number',
                            renderCell: (params) => {
                                return `${formatCurrency(params.value)}`;
                            }
                        },
                        {
                            field: 'total_amount', headerName: 'Total Amount', flex: .25, minWidth: 120, type: 'number',
                            renderCell: (params) => {
                                return `${formatCurrency(params.value)}`;
                            }
                        },
                        {
                            field: 'sale_date', headerName: 'Sold Date', flex: .2, minWidth: 100, type: 'date',
                            renderCell: (params) => {
                                return params.value ? dayjs(params.value).format('M/D/YY') : '';
                            }
                        }
                    ];
                    setMarketColumns(mc);

                    var mr = [];
                    var idCounter = 0;

                    // Add My Tickets (Open tickets) - Blue
                    response.data.open_tickets.forEach((row) => {
                        mr.push({
                            id: idCounter++,
                            section: row._id.section,
                            row: row._id.row,
                            qty: row.open_count,
                            price_per_ticket: row.avg_ticket,
                            total_amount: row.retail,
                            sale_date: null,
                            data_source: 'my_tickets'
                        });
                    });

                    // Add My Sales (Sold tickets) - Green
                    response.data.sold_tickets.forEach((row) => {
                        mr.push({
                            id: idCounter++,
                            section: row._id.section,
                            row: row._id.row,
                            qty: row.sold_count,
                            price_per_ticket: row.avg_ticket,
                            total_amount: row.total_sold,
                            sale_date: row._id.sale_date ? new Date(row._id.sale_date) : null,
                            data_source: 'my_sales'
                        });
                    });

                    // Add Market Listings - Different Blue  
                    if (response.data.datafeed_listings && response.data.datafeed_listings.length > 0) {
                        response.data.datafeed_listings.forEach((listing) => {
                            mr.push({
                                id: idCounter++,
                                section: listing.seat_section || 'N/A',
                                row: listing.seat_row || 'N/A',
                                qty: listing.quantity || 1,
                                price_per_ticket: listing.price_per_ticket || 0,
                                total_amount: (listing.quantity || 1) * (listing.price_per_ticket || 0),
                                sale_date: null,
                                data_source: 'market_listings'
                            });
                        });
                    }

                    // Add Market Sales - Different Green
                    if (response.data.datafeed_sales && response.data.datafeed_sales.length > 0) {
                        response.data.datafeed_sales.forEach((sale) => {
                            mr.push({
                                id: idCounter++,
                                section: sale.seat_section || 'N/A',
                                row: sale.seat_row || 'N/A',
                                qty: sale.quantity || 1,
                                price_per_ticket: sale.average_sold_price || 0,
                                total_amount: (sale.quantity || 1) * (sale.average_sold_price || 0),
                                sale_date: sale.sale_date ? new Date(sale.sale_date) : null,
                                data_source: 'market_sales'
                            });
                        });
                    }

                    setAllMarketRows(mr);
                    setMarketRows(mr);
                }).catch((err) => {
                    if (err.message !== 'canceled') {
                        console.log(err);
                    }
                });
            });
        }
    }

    function getSalesCustomerEventData(customer) {
        getAccessTokenSilently({
            audience: process.env.REACT_APP_AUTH0AUDIENCE,
            scope: process.env.REACT_APP_AUTH0SCOPE
        }).then((token) => {
            api.get(
                `${process.env.REACT_APP_BASEURL}/api/dashboard/event_customer_sold/${event_id}/${current_broker}/${customer}`,
                { headers: { Authorization: `Bearer ${token}` } }
            ).then((response) => {
                var c = [
                    { field: 'sale_date', headerName: 'Sale Date', flex: .25, minWidth: 100, type: 'date' },
                    { field: 'customer', headerName: 'Customer', flex: .25, minWidth: 100, type: 'string' },
                    { field: 'count', headerName: 'Sold Qty', flex: .25, minWidth: 50, type: 'number' },
                    { field: 'section', headerName: 'Section', flex: .25, minWidth: 50, type: 'string' },
                    { field: 'row', headerName: 'Row', flex: .25, minWidth: 50, type: 'string' },
                    { field: 'low', headerName: 'Low Seat', flex: .25, minWidth: 50, type: 'number' },
                    { field: 'high', headerName: 'High Seat', flex: .25, minWidth: 50, type: 'number' },
                    {
                        field: 'cost', headerName: 'Cost', flex: .25, minWidth: 100, type: 'number',
                        renderCell: (params) => {
                            return `${formatCurrency(params.value)}`;
                        }
                    },
                    {
                        field: 'sold', headerName: 'Sold', flex: .25, minWidth: 100, type: 'number',
                        renderCell: (params) => {
                            return `${formatCurrency(params.value)}`;
                        }
                    },
                    {
                        field: 'avg', headerName: 'Avg Ticket', flex: .25, minWidth: 100, type: 'number',
                        renderCell: (params) => {
                            return `${formatCurrency(params.value)}`;
                        }
                    },
                    {
                        field: 'profit', headerName: 'Profit/Loss', flex: .25, minWidth: 100, type: 'number',
                        renderCell: (params) => {
                            return `${formatCurrency(params.value)}`;
                        }
                    },
                    {
                        field: 'profit_percent', headerName: 'Profit Margin', flex: .25, minWidth: 100, type: 'number',
                        renderCell: (params) => {
                            return `${formatNumberFraction(params.value)}%`;
                        }
                    },
                ];
                setEColumns(c);

                var r = [];
                response.data.forEach((row, i) => {
                    r.push({
                        id: i,
                        sale_date: dayjs(row._id.sale_date.replace(/[Z]$/g, '')).toDate(),
                        customer: row._id.customer,
                        count: row.sold_count,
                        section: row._id.section,
                        row: row._id.row,
                        low: row.low_seat,
                        high: row.high_seat,
                        cost: row.cost,
                        sold: row.sold,
                        avg: row.avg_ticket,
                        profit: row.sold - row.cost,
                        profit_percent: ((row.sold - row.cost) / row.cost) * 100
                    });
                });
                setERows(r);
            }).catch((err) => {
                if (err.message !== 'canceled') {
                    console.log(err);
                }
            });

        });
    }


    function getSalesEventData(date) {
        getAccessTokenSilently({
            audience: process.env.REACT_APP_AUTH0AUDIENCE,
            scope: process.env.REACT_APP_AUTH0SCOPE
        }).then((token) => {
            api.get(
                `${process.env.REACT_APP_BASEURL}/api/dashboard/event_sold/${event_id}/${current_broker}/${date}`,
                { headers: { Authorization: `Bearer ${token}` } }
            ).then((response) => {
                var c = [
                    { field: 'sale_date', headerName: 'Sale Date', flex: .25, minWidth: 100, type: 'date' },
                    { field: 'customer', headerName: 'Customer', flex: .25, minWidth: 100, type: 'string' },
                    { field: 'count', headerName: 'Sold Qty', flex: .25, minWidth: 50, type: 'number' },
                    { field: 'section', headerName: 'Section', flex: .25, minWidth: 50, type: 'string' },
                    { field: 'row', headerName: 'Row', flex: .25, minWidth: 50, type: 'string' },
                    { field: 'low', headerName: 'Low Seat', flex: .25, minWidth: 50, type: 'number' },
                    { field: 'high', headerName: 'High Seat', flex: .25, minWidth: 50, type: 'number' },
                    {
                        field: 'cost', headerName: 'Cost', flex: .25, minWidth: 100, type: 'number',
                        renderCell: (params) => {
                            return `${formatCurrency(params.value)}`;
                        }
                    },
                    {
                        field: 'sold', headerName: 'Sold', flex: .25, minWidth: 100, type: 'number',
                        renderCell: (params) => {
                            return `${formatCurrency(params.value)}`;
                        }
                    },
                    {
                        field: 'avg', headerName: 'Avg Ticket', flex: .25, minWidth: 100, type: 'number',
                        renderCell: (params) => {
                            return `${formatCurrency(params.value)}`;
                        }
                    },
                    {
                        field: 'profit', headerName: 'Profit/Loss', flex: .25, minWidth: 100, type: 'number',
                        renderCell: (params) => {
                            return `${formatCurrency(params.value)}`;
                        }
                    },
                    {
                        field: 'profit_percent', headerName: 'Profit Margin', flex: .25, minWidth: 100, type: 'number',
                        renderCell: (params) => {
                            return `${formatNumberFraction(params.value)}%`;
                        }
                    },
                ];
                setEColumns(c);

                var r = [];
                response.data.forEach((row, i) => {
                    r.push({
                        id: i,
                        sale_date: dayjs(row._id.sale_date.replace(/[Z]$/g, '')).toDate(),
                        customer: row._id.customer,
                        count: row.sold_count,
                        section: row._id.section,
                        row: row._id.row,
                        low: row.low_seat,
                        high: row.high_seat,
                        cost: row.cost,
                        sold: row.sold,
                        avg: row.avg_ticket,
                        profit: row.sold - row.cost,
                        profit_percent: ((row.sold - row.cost) / row.cost) * 100
                    });
                });
                setERows(r);
            }).catch((err) => {
                if (err.message !== 'canceled') {
                    console.log(err);
                }
            });

        });
    }

    const getRowClassName = (params) => {
        const { data_source } = params.row;
        switch (data_source) {
            case 'my_tickets':
                return 'my-tickets-row';
            case 'my_sales':
                return 'my-sales-row';
            case 'market_listings':
                return 'market-listings-row';
            case 'market_sales':
                return 'market-sales-row';
            default:
                return '';
        }
    };

    const toggleDataSource = (dataSource) => {
        const newVisibility = {
            ...visibleDataSources,
            [dataSource]: !visibleDataSources[dataSource]
        };
        setVisibleDataSources(newVisibility);
        
        // Filter the market rows based on visibility
        if (allMarketRows) {
            const filteredRows = allMarketRows.filter(row => 
                newVisibility[row.data_source]
            );
            setMarketRows(filteredRows);
        }
    };

    useEffect(() => {
        getEvent();
    }, [event_id]);

    if (event_id && event !== null) {
        var max_customer_label = 60; // Minimum margin
        event.sold_by_customer.forEach((x, i) => {
            if (x._id) {
                // Use larger multiplier and add padding for better text visibility
                const labelWidth = (x._id.length * 8); // 8px per character
                if (labelWidth > max_customer_label) {
                    max_customer_label = labelWidth;
                }
            }
        });

        return (
            <span style={{ margin: '15px' }}>
                <Dialog
                    open={openDialog}
                    onClose={handleDialogClose}
                    fullWidth
                    maxWidth={'xl'}
                >
                    <DialogTitle>{clicked_date}</DialogTitle>
                    <IconButton
                        edge="start"
                        color="inherit"
                        onClick={handleDialogClose}
                        aria-label="close"
                        sx={{
                            position: 'absolute',
                            right: 8,
                            top: 8
                        }}
                    >
                        <CloseIcon />
                    </IconButton>
                    <DialogContent>
                        {
                            erows && ecolumns
                                ?
                                <DataGridPro
                                    slots={{
                                        toolbar: GridToolbar
                                    }}
                                    slotProps={{
                                        toolbar: {
                                            showQuickFilter: true,
                                            quickFilterProps: { debounceMs: 500 }
                                        }
                                    }}
                                    initialState={{
                                        density: "compact"
                                    }}
                                    rows={erows}
                                    columns={ecolumns}
                                    pagination
                                    sx={{ height: '75vh' }}
                                />
                                :
                                <Box
                                    component="main"
                                    sx={{ flexGrow: 1, p: 3, alignItems: 'center', textAlign: 'center' }}
                                >
                                    <CircularProgress size={60} thickness={4} />
                                </Box>
                        }
                    </DialogContent>
                </Dialog>
                <Grid container spacing={2} sx={{ flexGrow: 1 }} alignItems={'center'} alignContent={'center'}>
                    <Grid item xs={12} lg={6}>
                        <Box flexGrow={1}>
                            <Card elevation={5}>
                                <CardHeader
                                    title={formatCurrency(event.sold_by_customer.reduce((n, { total_sold }) => n + total_sold, 0))}
                                    subheader='Total Event Sales'
                                    titleTypographyProps={{ color: process.env.REACT_APP_COLOR1 }}
                                    subheaderTypographyProps={{}}
                                    sx={{ background: 'transparent' }}
                                />
                                <CardContent>
                                    <ResponsiveChartContainer
                                        height={250}
                                        margin={{
                                            left: max_customer_label,
                                            top: 0,
                                            bottom: 0,
                                            right: 0
                                        }}
                                        yAxis={[
                                            { scaleType: 'band', dataKey: "_id" }
                                        ]}
                                        series={[
                                            {
                                                type: 'bar',
                                                dataKey: 'total_sold',
                                                color: '#41A949',
                                                valueFormatter: (value) => (
                                                    formatCurrency(value) +
                                                    ' (' +
                                                    formatNumberFraction((value / event.sold_by_customer.reduce((n, { total_sold }) => n + total_sold, 0)) * 100)
                                                    + '%)'
                                                ),
                                                layout: 'horizontal'
                                            }
                                        ]}
                                        dataset={event.sold_by_customer}
                                    >
                                        <BarPlot
                                            borderRadius={10}

                                            onItemClick={(e, d) => {
                                                getSalesCustomerEventData(event.sold_by_customer[d.dataIndex]._id);
                                                setClickedDate(event.sold_by_customer[d.dataIndex]._id);
                                                handleDialogClickOpen();
                                            }}
                                        />
                                        <ChartsTooltip />
                                        <ChartsYAxis disableLine disableTicks position='left' />
                                    </ResponsiveChartContainer>
                                </CardContent>
                            </Card>
                        </Box>
                    </Grid>
                    <Grid item xs={12} lg={6}>
                        <Box flexGrow={1}>
                            <Card elevation={5}>
                                <CardHeader
                                    title={formatNumber(event.sold_by_day.reduce((n, { sold_count }) => n + sold_count, 0))}
                                    subheader='Total Event Tickets Sold'
                                    titleTypographyProps={{ color: process.env.REACT_APP_COLOR1 }}
                                    subheaderTypographyProps={{}}
                                    sx={{ background: 'transparent' }}
                                />
                                <CardContent>
                                    <ResponsiveChartContainer
                                        height={250}
                                        margin={{
                                            left: 0,
                                            top: 0,
                                            bottom: 0,
                                            right: 0
                                        }}
                                        xAxis={[
                                            {
                                                dataKey: '_id',
                                                scaleType: 'band',
                                                min: event.sold_by_day.length > 0 ? event.sold_by_day[0]._id : dayjs().toDate(),
                                                max: event.sold_by_day.length > 0 ? event.sold_by_day[event.sold_by_day.length - 1]._id : dayjs().toDate(),
                                                valueFormatter: (value) => { return dayjs(value).format('MM/DD/YY'); }
                                            },
                                        ]}
                                        yAxis={[
                                            {
                                                id: 'dollar',
                                                max: Math.max(...event.sold_by_day.map(o => o.avg_ticket)) + (Math.max(...event.sold_by_day.map(o => o.avg_ticket)) * .1)
                                            },
                                            {
                                                id: 'count',
                                                max: Math.max(...event.sold_by_day.map(o => o.sold_count)) + (Math.max(...event.sold_by_day.map(o => o.sold_count)) * .1)
                                            }
                                        ]}
                                        series={[
                                            {
                                                type: 'bar',
                                                dataKey: 'sold_count',
                                                color: '#41A949',
                                                label: 'Total Sold',
                                                valueFormatter: (value) => (formatNumber(value)),
                                                yAxisId: 'count'
                                            },
                                            {
                                                type: 'line',
                                                dataKey: 'high_ticket',
                                                color: '#006600',
                                                label: 'High Price',
                                                valueFormatter: (value) => (formatCurrency(value)),
                                                yAxisId: 'dollar'
                                            },
                                            {
                                                type: 'line',
                                                dataKey: 'low_ticket',
                                                color: '#fe5f55',
                                                label: 'Low Price',
                                                valueFormatter: (value) => (formatCurrency(value)),
                                                yAxisId: 'dollar'
                                            },
                                            {
                                                type: 'line',
                                                dataKey: 'avg_ticket',
                                                color: '#577399',
                                                label: 'Avg Price',
                                                valueFormatter: (value) => (formatCurrency(value)),
                                                yAxisId: 'dollar'
                                            }
                                        ]}
                                        dataset={event.sold_by_day}
                                    >
                                        <BarPlot
                                            borderRadius={10}

                                            onItemClick={(e, d) => {
                                                getSalesEventData(event.sold_by_day[d.dataIndex]._id);
                                                setClickedDate(dayjs(event.sold_by_day[d.dataIndex]._id).format('MM/DD/YYYY'));
                                                handleDialogClickOpen();
                                            }}
                                        />
                                        <LinePlot />
                                        <MarkPlot />
                                        <ChartsTooltip />
                                    </ResponsiveChartContainer>
                                </CardContent>
                            </Card>
                        </Box>
                    </Grid>
                </Grid>
                <br />
                <Grid container spacing={2} sx={{ flexGrow: 1 }} alignItems={'center'} alignContent={'center'}>
                    <Grid item xs={12} md={6} lg={3.25}>
                        <Box flexGrow={1}>
                            <Card elevation={5} sx={{ height: '375px' }}>
                                <CardHeader
                                    title='Ticket Stats'
                                    subheader={`${formatNumber(event.open_tickets.reduce((n, { open_count }) => n + open_count, 0))} Open Tickets`}
                                    sx={{ backgroundColor: 'gainsboro' }}
                                />
                                <CardContent>
                                    <TableContainer component={Box}>
                                        <Table size="small">
                                            <TableBody>
                                                <TableRow key={'rt'}>
                                                    <TableCell component="th" scope="row">Open Tickets (Mine)</TableCell>
                                                    <TableCell align="right">{formatNumber(event.open_tickets.reduce((n, { open_count }) => n + open_count, 0))}</TableCell>
                                                </TableRow>
                                                <TableRow key={'ws'}>
                                                    <TableCell component="th" scope="row">Open Tickets (All Vendors)</TableCell>
                                                    <TableCell align="right">{formatNumber(event.open_tickets_all.reduce((n, { open_count }) => n + open_count, 0))}</TableCell>
                                                </TableRow>
                                                <TableRow key={'rc'}>
                                                    <TableCell component="th" scope="row">Lowest Open Price</TableCell>
                                                    <TableCell align="right">{formatCurrency(Math.min(...event.open_tickets.map(o => o.low_ticket)))}</TableCell>
                                                </TableRow>
                                                <TableRow key={'tc'}>
                                                    <TableCell component="th" scope="row">Highest Open Price</TableCell>
                                                    <TableCell align="right">{formatCurrency(Math.max(...event.open_tickets.map(o => o.high_ticket)))}</TableCell>
                                                </TableRow>
                                                <TableRow key={'ts'}>
                                                    <TableCell component="th" scope="row">Total Open Value</TableCell>
                                                    <TableCell align="right">{formatCurrency(event.open_tickets.reduce((n, { retail }) => n + retail, 0))}</TableCell>
                                                </TableRow>
                                                <TableRow key={'apm'}>
                                                    <TableCell component="th" scope="row">Average Open Price (Mine)</TableCell>
                                                    <TableCell align="right">{formatCurrency(event.open_tickets.reduce((n, { avg_ticket }) => n + avg_ticket, 0) / event.open_tickets.length)}</TableCell>
                                                </TableRow>
                                                <TableRow key={'apa'}>
                                                    <TableCell component="th" scope="row">Average Open Price (All Vendors)</TableCell>
                                                    <TableCell align="right">{formatCurrency(event.open_tickets_all.reduce((n, { avg_ticket }) => n + avg_ticket, 0) / event.open_tickets_all.length)}</TableCell>
                                                </TableRow>
                                            </TableBody>
                                        </Table>
                                    </TableContainer>
                                </CardContent>
                            </Card>
                        </Box>
                    </Grid>
                    <Grid item xs={12} md={12} lg={8.75}>
                        <Box flexGrow={1} elevation={5}>
                            <Card elevation={5} sx={{ width: '100%', height: '375px' }}>
                                <DataGridPro
                                    density="compact"
                                    rows={rows}
                                    columns={columns}
                                    pagination
                                />
                            </Card>
                        </Box>
                    </Grid>
                </Grid>
                <br />
                <Grid container spacing={2} sx={{ flexGrow: 1 }} alignItems={'center'} alignContent={'center'}>
                    <Grid item xs={12} md={6} lg={3.25}>
                        <Box flexGrow={1}>
                            <Card elevation={5} sx={{ height: '375px' }}>
                                <CardHeader
                                    title='Profit/Loss Details'
                                    subheader={event.profit >= 0 ? `${formatCurrency(event.profit)} Profit` : `${formatCurrency(event.profit)} Loss`}
                                    subheaderTypographyProps={event.profit >= 0 ? { color: '#41A949' } : { color: process.env.REACT_APP_COLOR2 }}
                                    sx={{ backgroundColor: 'gainsboro' }}
                                />
                                <CardContent>
                                    <TableContainer component={Box}>
                                        <Table size="small">
                                            <TableBody>
                                                <TableRow key={'days'}>
                                                    <TableCell component="th" scope="row">Days Left</TableCell>
                                                    <TableCell align="right">{formatNumberFraction(dayjs(event._id.event_datetime).diff(dayjs(), 'day', true))}</TableCell>
                                                </TableRow>
                                                <TableRow key={'rt'}>
                                                    <TableCell component="th" scope="row">Remaining Potential Retail</TableCell>
                                                    <TableCell align="right">{event.hasOwnProperty('p_l') ? formatCurrency(event.p_l.remaining_r) : formatCurrency(0)}</TableCell>
                                                </TableRow>
                                                <TableRow key={'rc'}>
                                                    <TableCell component="th" scope="row">Remaining Cost</TableCell>
                                                    <TableCell align="right">{event.hasOwnProperty('p_l') ? formatCurrency(event.p_l.remaining_c) : formatCurrency(0)}</TableCell>
                                                </TableRow>
                                                <TableRow key={'tc'}>
                                                    <TableCell component="th" scope="row">Total Cost</TableCell>
                                                    <TableCell align="right">{formatCurrency(event.cost)}</TableCell>
                                                </TableRow>
                                                <TableRow key={'ts'}>
                                                    <TableCell component="th" scope="row">Total Sold</TableCell>
                                                    <TableCell align="right">{formatCurrency(event.sold)}</TableCell>
                                                </TableRow>
                                                <TableRow key={'tpl'}>
                                                    <TableCell component="th" scope="row">Total P/L</TableCell>
                                                    <TableCell align="right">{formatCurrency(event.profit)}</TableCell>
                                                </TableRow>
                                            </TableBody>
                                        </Table>
                                    </TableContainer>
                                </CardContent>
                            </Card>
                        </Box>
                    </Grid>
                    <Grid item xs={12} md={12} lg={8.75}>
                        <Box flexGrow={1}>
                            <Card elevation={5} sx={{ width: '100%', height: '375px' }}>
                                {/* Color Legend */}
                                <Box sx={{ padding: 2, paddingBottom: 1 }}>
                                    <Stack direction="row" spacing={2} sx={{ marginBottom: 1 }}>
                                        <Chip 
                                            label="My Tickets" 
                                            size="small" 
                                            clickable
                                            onClick={() => toggleDataSource('my_tickets')}
                                            sx={{ 
                                                backgroundColor: visibleDataSources.my_tickets ? '#3f51b5' : '#e0e0e0', 
                                                color: visibleDataSources.my_tickets ? 'white' : '#757575',
                                                fontSize: '0.75rem',
                                                '&:hover': {
                                                    backgroundColor: visibleDataSources.my_tickets ? '#303f9f' : '#d0d0d0'
                                                }
                                            }} 
                                        />
                                        <Chip 
                                            label="My Sales" 
                                            size="small" 
                                            clickable
                                            onClick={() => toggleDataSource('my_sales')}
                                            sx={{ 
                                                backgroundColor: visibleDataSources.my_sales ? '#4caf50' : '#e0e0e0', 
                                                color: visibleDataSources.my_sales ? 'white' : '#757575',
                                                fontSize: '0.75rem',
                                                '&:hover': {
                                                    backgroundColor: visibleDataSources.my_sales ? '#388e3c' : '#d0d0d0'
                                                }
                                            }} 
                                        />
                                        <Chip 
                                            label="Market Listings" 
                                            size="small" 
                                            clickable
                                            onClick={() => toggleDataSource('market_listings')}
                                            sx={{ 
                                                backgroundColor: visibleDataSources.market_listings ? '#ff9800' : '#e0e0e0', 
                                                color: visibleDataSources.market_listings ? 'white' : '#757575',
                                                fontSize: '0.75rem',
                                                '&:hover': {
                                                    backgroundColor: visibleDataSources.market_listings ? '#f57c00' : '#d0d0d0'
                                                }
                                            }} 
                                        />
                                        <Chip 
                                            label="Market Sales" 
                                            size="small" 
                                            clickable
                                            onClick={() => toggleDataSource('market_sales')}
                                            sx={{ 
                                                backgroundColor: visibleDataSources.market_sales ? '#9c27b0' : '#e0e0e0', 
                                                color: visibleDataSources.market_sales ? 'white' : '#757575',
                                                fontSize: '0.75rem',
                                                '&:hover': {
                                                    backgroundColor: visibleDataSources.market_sales ? '#7b1fa2' : '#d0d0d0'
                                                }
                                            }} 
                                        />
                                    </Stack>
                                </Box>
                                {
                                    marketRows && marketColumns
                                        ?
                                        <DataGridPro
                                            density="compact"
                                            rows={marketRows}
                                            columns={marketColumns}
                                            pagination
                                            getRowClassName={getRowClassName}
                                            sx={{ 
                                                height: '325px',
                                                '& .my-tickets-row': {
                                                    backgroundColor: '#e8eaf6',
                                                    '&:hover': {
                                                        backgroundColor: '#c5cae9',
                                                    },
                                                },
                                                '& .my-sales-row': {
                                                    backgroundColor: '#e8f5e8',
                                                    '&:hover': {
                                                        backgroundColor: '#c8e6c9',
                                                    },
                                                },
                                                '& .market-listings-row': {
                                                    backgroundColor: '#fff3e0',
                                                    '&:hover': {
                                                        backgroundColor: '#ffe0b2',
                                                    },
                                                },
                                                '& .market-sales-row': {
                                                    backgroundColor: '#f3e5f5',
                                                    '&:hover': {
                                                        backgroundColor: '#e1bee7',
                                                    },
                                                },
                                            }}
                                        />
                                        :
                                        <Box
                                            component="main"
                                            sx={{ flexGrow: 1, p: 3, alignItems: 'center', textAlign: 'center' }}
                                        >
                                            <CircularProgress size={60} thickness={4} />
                                        </Box>
                                }
                            </Card>
                        </Box>
                    </Grid>
                </Grid>
            </span>
        );
    } else {
        return (
            <Box
                component="main"
                sx={{ flexGrow: 1, p: 3, alignItems: 'center', textAlign: 'center' }}
            >
                <CircularProgress size={60} thickness={4} />
            </Box>
        );
    }
}

export default Events;